import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { partnerTransactionsApi } from '@/api/partnerTransactions';
import { 
  Transaction, 
  TransactionFilters, 
  CreateTransactionRequest, 
  UpdateTransactionRequest 
} from '@/api/partnerTransactions/types';
import { RootState } from '@/app/rootReducer';

interface TransactionManagementState {
  transactions: Transaction[];
  selectedTransaction: Transaction | null;
  loading: boolean;
  error: string | null;
  filters: TransactionFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
  exportLoading: boolean;
}

const initialState: TransactionManagementState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  error: null,
  filters: {
    searchTerm: '',
    types: [],
    entityTypes: [],
    quarter: undefined,
    year: undefined,
    startDate: undefined,
    endDate: undefined,
  },
  pagination: {
    page: 1,
    limit: 25,
    total: 0,
  },
  exportLoading: false,
};

// Async thunks
export const fetchAllTransactionsAction = createAsyncThunk(
  'transactionManagement/fetchAllTransactions',
  async (filters?: TransactionFilters) => {
    const response = await partnerTransactionsApi.fetchTransactions(filters);
    return response;
  }
);

export const fetchTransactionDetailsAction = createAsyncThunk(
  'transactionManagement/fetchTransactionDetails',
  async (transactionId: string) => {
    const response = await partnerTransactionsApi.fetchTransaction(transactionId);
    return response;
  }
);

export const createNewTransactionAction = createAsyncThunk(
  'transactionManagement/createNewTransaction',
  async (data: CreateTransactionRequest) => {
    const response = await partnerTransactionsApi.createTransaction(data);
    return response;
  }
);

export const updateTransactionDetailsAction = createAsyncThunk(
  'transactionManagement/updateTransactionDetails',
  async ({ id, data }: { id: string; data: UpdateTransactionRequest }) => {
    const response = await partnerTransactionsApi.updateTransaction(id, data);
    return response;
  }
);

export const deleteTransactionRecordAction = createAsyncThunk(
  'transactionManagement/deleteTransactionRecord',
  async (transactionId: string) => {
    await partnerTransactionsApi.deleteTransaction(transactionId);
    return transactionId;
  }
);

export const exportTransactionsToCSVAction = createAsyncThunk(
  'transactionManagement/exportTransactionsToCSV',
  async (filters?: TransactionFilters) => {
    const blob = await partnerTransactionsApi.exportTransactions(filters);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  }
);

export const downloadTransactionAttachmentAction = createAsyncThunk(
  'transactionManagement/downloadTransactionAttachment',
  async ({ transactionId, fileName }: { transactionId: string; fileName: string }) => {
    const blob = await partnerTransactionsApi.downloadAttachment(transactionId);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  }
);

export const updateTransactionStatusAction = createAsyncThunk(
  'transactionManagement/updateTransactionStatus',
  async ({ transactionId, status }: { transactionId: string; status: string }) => {
    const response = await partnerTransactionsApi.updateTransactionStatus(transactionId, status);
    return response;
  }
);

const transactionManagementSlice = createSlice({
  name: 'transactionManagement',
  initialState,
  reducers: {
    setSelectedTransaction: (state, action: PayloadAction<Transaction | null>) => {
      state.selectedTransaction = action.payload;
    },
    updateFilters: (state, action: PayloadAction<Partial<TransactionFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Reset to first page when filters change
      if (Object.keys(action.payload).length > 0 && !action.payload.hasOwnProperty('page')) {
        state.pagination.page = 1;
      }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.page = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<{ page: number; limit: number }>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all transactions
    builder
      .addCase(fetchAllTransactionsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllTransactionsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 25,
          total: action.payload.total || 0,
        };
      })
      .addCase(fetchAllTransactionsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transactions';
      });

    // Fetch transaction details
    builder
      .addCase(fetchTransactionDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTransaction = action.payload;
      })
      .addCase(fetchTransactionDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch transaction details';
      });

    // Create new transaction
    builder
      .addCase(createNewTransactionAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewTransactionAction.fulfilled, (state, action) => {
        state.loading = false;
        // Add to the beginning of the list
        state.transactions.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createNewTransactionAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create transaction';
      });

    // Update transaction
    builder
      .addCase(updateTransactionDetailsAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionDetailsAction.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transactions.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
        if (state.selectedTransaction?.id === action.payload.id) {
          state.selectedTransaction = action.payload;
        }
      })
      .addCase(updateTransactionDetailsAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update transaction';
      });

    // Delete transaction
    builder
      .addCase(deleteTransactionRecordAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTransactionRecordAction.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = state.transactions.filter(t => t.id !== action.payload);
        state.pagination.total -= 1;
        if (state.selectedTransaction?.id === action.payload) {
          state.selectedTransaction = null;
        }
      })
      .addCase(deleteTransactionRecordAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete transaction';
      });

    // Export transactions
    builder
      .addCase(exportTransactionsToCSVAction.pending, (state) => {
        state.exportLoading = true;
        state.error = null;
      })
      .addCase(exportTransactionsToCSVAction.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportTransactionsToCSVAction.rejected, (state, action) => {
        state.exportLoading = false;
        state.error = action.error.message || 'Failed to export transactions';
      });

    // Update transaction status
    builder
      .addCase(updateTransactionStatusAction.pending, (state) => {
        // Don't show loading for status updates
        state.error = null;
      })
      .addCase(updateTransactionStatusAction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
        if (state.selectedTransaction?.id === action.payload.id) {
          state.selectedTransaction = action.payload;
        }
      })
      .addCase(updateTransactionStatusAction.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update transaction status';
      });
  },
});

export const { 
  setSelectedTransaction, 
  updateFilters, 
  resetFilters, 
  setPagination,
  clearError 
} = transactionManagementSlice.actions;

// Selectors
export const selectAllTransactions = (state: RootState) => state.partnerNetwork.transactionManagement.transactions;
export const selectSelectedTransaction = (state: RootState) => state.partnerNetwork.transactionManagement.selectedTransaction;
export const selectTransactionLoading = (state: RootState) => state.partnerNetwork.transactionManagement.loading;
export const selectTransactionError = (state: RootState) => state.partnerNetwork.transactionManagement.error;
export const selectTransactionFilters = (state: RootState) => state.partnerNetwork.transactionManagement.filters;
export const selectTransactionPagination = (state: RootState) => state.partnerNetwork.transactionManagement.pagination;
export const selectExportLoading = (state: RootState) => state.partnerNetwork.transactionManagement.exportLoading;

export default transactionManagementSlice.reducer;